/**
 * Shared printer discover/connect logic.
 *
 * Backs both the Settings → Printers `PrinterListDialog` and the inline
 * available-printers list inside the Printer Helper's troubleshoot view, so the
 * scan/connect behaviour (interface scope, BLE/USB opt-in, connect + persist,
 * snackbars) lives in exactly one place.
 *
 * Bluetooth guard: when `guardBluetooth` is set and a Bluetooth interface is in
 * scope, the radio is checked once before scanning. If it's off, the Bluetooth
 * interfaces are dropped from the scan (LAN/USB still run) and `bluetoothOff` is
 * raised so the caller can prompt the user — it never silently rescans.
 */
import { useState, useCallback } from 'react';

import { isBluetoothEnabled } from 'src/hooks/use-device-permissions';

import { starPrintService } from 'src/services/printers';
import type {
  StarPrinter,
  StarPrinterInterface,
  StarPrintConfigurationIssue,
} from 'src/services/printers';

import { useSnackbar } from 'src/components/snackbar';

// ----------------------------------------------------------------------

interface UsePrinterDiscoveryOptions {
  /** Check the Bluetooth radio before scanning a Bluetooth interface. */
  guardBluetooth?: boolean;
}

export interface UsePrinterDiscoveryResult {
  printers: StarPrinter[];
  issues: StarPrintConfigurationIssue[];
  loading: boolean;
  includeBle: boolean;
  includeUsb: boolean;
  setIncludeBle: (value: boolean) => void;
  setIncludeUsb: (value: boolean) => void;
  connectingId: string | null;
  /** A Bluetooth interface was in scope but the radio is off. */
  bluetoothOff: boolean;
  /** Run a discovery scan with the current interface scope. */
  discover: () => Promise<void>;
  /** Connect to + persist `printer` as the default; resolves true on success. */
  connect: (printer: StarPrinter) => Promise<boolean>;
}

export function usePrinterDiscovery(
  options: UsePrinterDiscoveryOptions = {}
): UsePrinterDiscoveryResult {
  const { guardBluetooth = false } = options;
  const { enqueueSnackbar } = useSnackbar();

  const [printers, setPrinters] = useState<StarPrinter[]>([]);
  const [issues, setIssues] = useState<StarPrintConfigurationIssue[]>([]);
  const [loading, setLoading] = useState(false);
  // BLE + USB are scanned by default so cashiers don't have to flip toggles to
  // find their printer; LAN + Bluetooth Classic are always included as well.
  const [includeBle, setIncludeBle] = useState(true);
  const [includeUsb, setIncludeUsb] = useState(true);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [bluetoothOff, setBluetoothOff] = useState(false);

  const discover = useCallback(async () => {
    setLoading(true);
    setBluetoothOff(false);
    try {
      // LAN + Bluetooth Classic always; BLE and USB are opt-in (model-dependent on iOS).
      let interfaceTypes: StarPrinterInterface[] = ['lan', 'bluetooth'];
      if (includeBle) interfaceTypes.push('ble');
      if (includeUsb) interfaceTypes.push('usb');

      // Don't scan Bluetooth interfaces while the radio is off — prompt instead.
      const scansBluetooth = interfaceTypes.some((t) => t === 'bluetooth' || t === 'ble');
      if (guardBluetooth && scansBluetooth && !(await isBluetoothEnabled())) {
        setBluetoothOff(true);
        interfaceTypes = interfaceTypes.filter((t) => t !== 'bluetooth' && t !== 'ble');
      }

      const result = await starPrintService.discoverPrinters({ interfaceTypes });
      setPrinters(result.printers);
      setIssues(result.configurationIssues);
      if (
        !bluetoothOff &&
        result.printers.length === 0 &&
        result.configurationIssues.length === 0
      ) {
        enqueueSnackbar('No printers found', { variant: 'info' });
      }
    } catch (error) {
      console.error('[usePrinterDiscovery] discovery failed:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Printer discovery failed', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
    // `bluetoothOff` is read for the snackbar guard only; excluded to avoid a stale-closure loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeBle, includeUsb, guardBluetooth, enqueueSnackbar]);

  const connect = useCallback(
    async (printer: StarPrinter): Promise<boolean> => {
      setConnectingId(printer.identifier);
      try {
        await starPrintService.connectPrinter(printer.connection);
        await starPrintService.saveDefaultPrinter(printer);
        enqueueSnackbar(`Connected to ${printer.name ?? printer.model}`, { variant: 'success' });
        return true;
      } catch (error) {
        console.error('[usePrinterDiscovery] connect failed:', error);
        enqueueSnackbar(error instanceof Error ? error.message : 'Could not connect to printer', {
          variant: 'error',
        });
        return false;
      } finally {
        setConnectingId(null);
      }
    },
    [enqueueSnackbar]
  );

  return {
    printers,
    issues,
    loading,
    includeBle,
    includeUsb,
    setIncludeBle,
    setIncludeUsb,
    connectingId,
    bluetoothOff,
    discover,
    connect,
  };
}
